/* eslint-disable max-lines */
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import mermaid, { type MermaidConfig } from "mermaid"
import { useTranslation } from "react-i18next"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { vs, vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism"
import { toast } from "sonner"

import { useTheme } from "@/app/providers/theme-provider"
import {
  Check,
  Code,
  Copy,
  Download,
  Eye,
  Maximize2,
  Minus,
  Palette,
  Pencil,
  Plus,
  RotateCcw,
  WrapText,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { MermaidEditorDialog } from "@/features/mermaid-editor/components/mermaid-editor-dialog"
import { useSessionId } from "@/hooks/use-session-id"
import { renderMermaidExclusive } from "@/lib/mermaid-render-lock"
import { cn } from "@/lib/utils"
import { ChatService } from "@/services/chat-service"

import { MermaidPlaceholder } from "./mermaid-placeholder"
import { getCachedMermaidSvg, setCachedMermaidSvg } from "./mermaid-svg-cache"

export type MermaidBlockProps = {
  code: string
  isStreaming?: boolean
  messageId?: string
  sessionId?: string
  rawMessageContent?: string
  onMermaidBlockUpdate?: (oldCode: string, newCode: string) => Promise<void>
  canEdit?: boolean
}

type MermaidTheme = "default" | "dark" | "neutral" | "forest" | "base"

type ThemeOption = {
  id: MermaidTheme
  label: string
  colors: string[]
}

const MERMAID_THEMES: ThemeOption[] = [
  { id: "default", label: "Default", colors: ["#326CE5", "#9370DB", "#FFD700"] },
  { id: "dark", label: "Dark", colors: ["#1f2020", "#67B8DE", "#BB6ACD"] },
  { id: "neutral", label: "Neutral", colors: ["#333333", "#666666", "#999999"] },
  { id: "forest", label: "Forest", colors: ["#13540c", "#4dbd33", "#91E37E"] },
  { id: "base", label: "Base", colors: ["#ECECFF", "#9370DB", "#333333"] },
]

const MIN_ZOOM = 0.25
const MAX_ZOOM = 8
const ZOOM_STEP = 0.15
const AUTO_FIT_MARGIN = 0.9

function getPrimaryDocumentFontFamily(): string | null {
  if (typeof window === "undefined") return null
  const fontFamily = window.getComputedStyle(document.body).fontFamily
  if (!fontFamily) return null

  const [primary] = fontFamily.split(",")
  const trimmed = primary.trim()
  return trimmed || null
}

async function waitForFontLoadOrTimeout(timeoutMs: number): Promise<boolean> {
  if (typeof document === "undefined") return false
  const fonts = document.fonts

  const primaryFontFamily = getPrimaryDocumentFontFamily()
  if (!primaryFontFamily) return false

  const fontSpec = `16px ${primaryFontFamily}`
  if (fonts.check(fontSpec)) return false

  let didTimeout = false
  await Promise.race([
    fonts.load(fontSpec).catch(() => []),
    new Promise<void>((resolve) => {
      setTimeout(() => {
        didTimeout = true
        resolve()
      }, timeoutMs)
    }),
  ])

  return didTimeout
}

function makeRenderId(reactId: string): string {
  return `mermaid-${reactId.replace(/:/g, "")}-${Date.now()}`
}

function chatMermaidConfig(theme: MermaidTheme): MermaidConfig {
  return {
    startOnLoad: false,
    theme: theme,
    // "strict" (not "loose"): Mermaid sanitizes its rendered SVG (HTML in labels
    // encoded, click/script stripped) before we inject it via dangerouslySetInnerHTML
    // into the main app DOM. With shared artifacts a diagram can come from another
    // user, so an unsanitized "loose" render would be a stored-XSS vector.
    securityLevel: "strict",
    fontFamily: "inherit",
    // Avoid <foreignObject> labels so downloaded SVG/PNG renders correctly in Preview/canvas.
    htmlLabels: false,
    flowchart: { htmlLabels: false, useMaxWidth: false },
    gantt: { useMaxWidth: false },
    sequence: { useMaxWidth: false },
  }
}

function isMermaidCodeComplete(code: string): boolean {
  const trimmed = code.trim()
  if (!trimmed) return false

  const diagramTypes = [
    "graph",
    "flowchart",
    "sequenceDiagram",
    "classDiagram",
    "stateDiagram",
    "erDiagram",
    "journey",
    "gantt",
    "pie",
    "quadrantChart",
    "requirementDiagram",
    "gitGraph",
    "mindmap",
    "timeline",
    "sankey",
    "xychart",
    "block",
  ]

  const hasValidStart = diagramTypes.some(
    (type) => trimmed.startsWith(type) || trimmed.startsWith(`%%{`)
  )

  if (!hasValidStart) return false

  if (/--!?>\s*$/.test(trimmed)) return false
  if (/---\s*$/.test(trimmed)) return false
  if (/-\.\s*$/.test(trimmed)) return false
  if (/==>\s*$/.test(trimmed)) return false
  if (/--!?>\|[^|]*$/.test(trimmed)) return false

  const openBrackets = (trimmed.match(/\[/g) || []).length
  const closeBrackets = (trimmed.match(/\]/g) || []).length
  if (openBrackets > closeBrackets) return false

  const openParens = (trimmed.match(/\(/g) || []).length
  const closeParens = (trimmed.match(/\)/g) || []).length
  if (openParens > closeParens) return false

  const openBraces = (trimmed.match(/\{/g) || []).length
  const closeBraces = (trimmed.match(/\}/g) || []).length
  if (openBraces > closeBraces) return false

  const doubleQuotes = (trimmed.match(/"/g) || []).length
  if (doubleQuotes % 2 !== 0) return false

  const lines = trimmed.split("\n").filter((line) => line.trim())
  if (lines.length < 2) return false

  return true
}

type ZoomPanState = {
  scale: number
  translateX: number
  translateY: number
}

function parseSvgNumericAttribute(attributeValue: string | null): number | null {
  if (!attributeValue) return null

  const trimmed = attributeValue.trim()
  if (!trimmed || trimmed === "100%") return null

  const parsed = Number.parseFloat(trimmed)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return parsed
}

function getSvgIntrinsicSize(svgElement: SVGSVGElement): { width: number; height: number } | null {
  const width = parseSvgNumericAttribute(svgElement.getAttribute("width"))
  const height = parseSvgNumericAttribute(svgElement.getAttribute("height"))

  if (width && height) return { width, height }

  const viewBox = svgElement.getAttribute("viewBox")
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/).map((part) => Number.parseFloat(part))
    if (parts.length === 4 && parts.every((part) => Number.isFinite(part))) {
      const [, , viewBoxWidth, viewBoxHeight] = parts
      if (viewBoxWidth > 0 && viewBoxHeight > 0) {
        return { width: viewBoxWidth, height: viewBoxHeight }
      }
    }
  }

  try {
    const bbox = svgElement.getBBox()
    if (bbox.width > 0 && bbox.height > 0) return { width: bbox.width, height: bbox.height }
  } catch {
    // ignore
  }

  return null
}

function ensureSvgNamespaces(svgElement: SVGSVGElement) {
  if (!svgElement.getAttribute("xmlns")) {
    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg")
  }
  if (!svgElement.getAttribute("xmlns:xlink")) {
    svgElement.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink")
  }
}

type SvgExportPayload = {
  svgForDataUri: string
  svgForFile: string
  width: number
  height: number
}

function getSvgExportPayload(svg: string): SvgExportPayload {
  const parsed = new DOMParser().parseFromString(svg, "image/svg+xml")
  const svgRoot = parsed.querySelector("svg")
  if (!(svgRoot instanceof SVGSVGElement)) {
    throw new Error("SVG root not found")
  }

  const svgClone = document.importNode(svgRoot, true)
  ensureSvgNamespaces(svgClone)

  const measurementContainer = document.createElement("div")
  measurementContainer.style.position = "fixed"
  measurementContainer.style.left = "-10000px"
  measurementContainer.style.top = "-10000px"
  measurementContainer.style.width = "0"
  measurementContainer.style.height = "0"
  measurementContainer.style.overflow = "hidden"
  measurementContainer.style.visibility = "hidden"
  measurementContainer.style.pointerEvents = "none"

  document.body.appendChild(measurementContainer)
  measurementContainer.appendChild(svgClone)

  const fallbackSize = { width: 800, height: 600 }
  const size = getSvgIntrinsicSize(svgClone) ?? fallbackSize

  if (!svgClone.getAttribute("viewBox")) {
    try {
      const bbox = svgClone.getBBox()
      if (bbox.width > 0 && bbox.height > 0) {
        svgClone.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`)
      } else {
        svgClone.setAttribute("viewBox", `0 0 ${size.width} ${size.height}`)
      }
    } catch {
      svgClone.setAttribute("viewBox", `0 0 ${size.width} ${size.height}`)
    }
  }

  svgClone.setAttribute("width", `${size.width}`)
  svgClone.setAttribute("height", `${size.height}`)

  const svgForDataUri = new XMLSerializer().serializeToString(svgClone)
  try {
    measurementContainer.remove()
  } catch {
    // Container may have been removed already, ignore
  }

  const svgForFile = `<?xml version="1.0" encoding="UTF-8"?>\n${svgForDataUri}`
  return { svgForDataUri, svgForFile, width: size.width, height: size.height }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  try {
    document.body.appendChild(link)
    link.click()
  } finally {
    try {
      if (link.parentNode === document.body) {
        document.body.removeChild(link)
      }
    } catch {
      // Element may have been removed already, ignore
    }
    URL.revokeObjectURL(url)
  }
}

function getElementContentBoxSize(element: HTMLElement): { width: number; height: number } {
  const rect = element.getBoundingClientRect()
  const styles = window.getComputedStyle(element)
  const paddingX = Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight)
  const paddingY = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom)

  return {
    width: Math.max(0, rect.width - paddingX),
    height: Math.max(0, rect.height - paddingY),
  }
}

function useMermaidZoomPan(containerRef: React.RefObject<HTMLDivElement | null>) {
  const defaultStateRef = useRef<ZoomPanState>({
    scale: 1,
    translateX: 0,
    translateY: 0,
  })
  const [state, setState] = useState<ZoomPanState>(defaultStateRef.current)
  const isDraggingRef = useRef(false)
  const lastPosition = useRef({ x: 0, y: 0 })

  const setTransform = useCallback(
    (next: ZoomPanState, options?: { setAsDefault?: boolean }) => {
      if (options?.setAsDefault) {
        defaultStateRef.current = next
      }
      setState(next)
    },
    [setState]
  )

  const handleWheel = useCallback((event: WheelEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
    setState((prev) => ({
      ...prev,
      scale: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev.scale + delta)),
    }))
  }, [])

  const handleMouseDown = useCallback((event: MouseEvent) => {
    if (event.button !== 0) return
    isDraggingRef.current = true
    lastPosition.current = { x: event.clientX, y: event.clientY }
    event.preventDefault()
  }, [])

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDraggingRef.current) return
    const deltaX = event.clientX - lastPosition.current.x
    const deltaY = event.clientY - lastPosition.current.y
    lastPosition.current = { x: event.clientX, y: event.clientY }
    setState((prev) => ({
      ...prev,
      translateX: prev.translateX + deltaX,
      translateY: prev.translateY + deltaY,
    }))
  }, [])

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  const reset = useCallback(() => {
    setState(defaultStateRef.current)
  }, [setState])

  const zoomIn = useCallback(() => {
    setState((prev) => ({
      ...prev,
      scale: Math.min(MAX_ZOOM, prev.scale + ZOOM_STEP),
    }))
  }, [])

  const zoomOut = useCallback(() => {
    setState((prev) => ({
      ...prev,
      scale: Math.max(MIN_ZOOM, prev.scale - ZOOM_STEP),
    }))
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener("wheel", handleWheel, { passive: false })
    container.addEventListener("mousedown", handleMouseDown)
    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)

    return () => {
      container.removeEventListener("wheel", handleWheel)
      container.removeEventListener("mousedown", handleMouseDown)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [containerRef, handleWheel, handleMouseDown, handleMouseMove, handleMouseUp])

  return { state, reset, zoomIn, zoomOut, isDraggingRef, setTransform, defaultStateRef }
}

type MermaidViewerProps = {
  svg: string
  isModal?: boolean
  showControls?: boolean
  autoFit?: boolean
  onExpand?: () => void
  onEdit?: () => void
  onCopy?: () => void
  onDownloadSvg?: () => void
  onDownloadPng?: () => void
  onThemeChange?: (theme: MermaidTheme) => void
  onShowCode?: () => void
  selectedTheme?: MermaidTheme
}

function MermaidViewer({
  svg,
  isModal = false,
  showControls = true,
  autoFit = false,
  onExpand,
  onEdit,
  onCopy,
  onDownloadSvg,
  onDownloadPng,
  onThemeChange,
  onShowCode,
  selectedTheme = "default",
}: MermaidViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const didAutoFitRef = useRef(false)
  const { state, reset, zoomIn, zoomOut, setTransform, defaultStateRef } =
    useMermaidZoomPan(containerRef)
  const { t } = useTranslation()
  const [isHovered, setIsHovered] = useState(false)

  const zoomPercentage = Math.round(state.scale * 100)
  const isAtDefaultView =
    state.scale === defaultStateRef.current.scale &&
    state.translateX === defaultStateRef.current.translateX &&
    state.translateY === defaultStateRef.current.translateY
  const showHoverControls = isHovered || isModal

  const fitToView = useCallback(() => {
    const viewport = viewportRef.current
    const container = containerRef.current
    if (!container) return false

    const svgElement = container.querySelector("svg")
    if (!(svgElement instanceof SVGSVGElement)) return false

    const sizeSource = viewport ?? container
    const available = getElementContentBoxSize(sizeSource)
    if (available.width <= 0 || available.height <= 0) return false

    const svgSize = getSvgIntrinsicSize(svgElement)
    if (!svgSize) return false

    const fitScale =
      Math.min(available.width / svgSize.width, available.height / svgSize.height) * AUTO_FIT_MARGIN
    if (!Number.isFinite(fitScale) || fitScale <= 0) return false

    const clampedScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, fitScale))
    setTransform({ scale: clampedScale, translateX: 0, translateY: 0 }, { setAsDefault: true })
    return true
  }, [setTransform])

  const runAutoFit = useCallback(() => {
    let tries = 0
    const maxTries = 10

    const tick = () => {
      tries += 1
      if (fitToView()) return
      if (tries < maxTries) requestAnimationFrame(tick)
    }

    requestAnimationFrame(tick)
  }, [fitToView])

  // Fit before the first paint when layout is already measurable (e.g. a
  // remount served from the SVG cache), so the diagram never shows unscaled;
  // otherwise retry over the next frames.
  useLayoutEffect(() => {
    if (!autoFit) return
    if (didAutoFitRef.current) return
    didAutoFitRef.current = true
    if (!fitToView()) runAutoFit()
  }, [autoFit, fitToView, runAutoFit])

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        ref={containerRef}
        className={cn(
          "bg-background overflow-hidden rounded-xl",
          isModal ? "min-h-[50vh]" : "min-h-52",
          "cursor-grab select-none active:cursor-grabbing"
        )}
      >
        <div
          ref={viewportRef}
          className="flex min-h-full w-full items-center justify-center p-8"
          style={{
            transform: `scale(${state.scale}) translate(${state.translateX / state.scale}px, ${state.translateY / state.scale}px)`,
            transformOrigin: "center center",
          }}
        >
          <div
            className="mermaid-diagram [&_.edgeLabel]:overflow-visible [&_.nodeLabel]:overflow-visible [&_svg]:max-w-none [&_svg]:overflow-visible [&_text]:overflow-visible"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </div>
      </div>

      {showControls && (
        <div
          className={cn(
            "bg-background/90 ring-border absolute top-3 right-3 flex items-center gap-1 rounded-lg p-1 shadow-lg ring-1 backdrop-blur-sm transition-opacity duration-200",
            showHoverControls ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="text-muted-foreground hover:text-foreground size-8"
                    size="icon"
                    variant="ghost"
                  >
                    <Palette className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t("answer.mermaid.theme")}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-40">
              {MERMAID_THEMES.map((themeOption) => (
                <DropdownMenuItem
                  key={themeOption.id}
                  className="flex items-center justify-between gap-2"
                  onClick={() => onThemeChange?.(themeOption.id)}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {themeOption.colors.map((color, colorIndex) => (
                        <div
                          key={colorIndex}
                          className="border-border/50 size-3 rounded-sm border"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <span className="text-sm">{themeOption.label}</span>
                  </div>
                  {selectedTheme === themeOption.id && <Check className="size-3.5" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {onEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="text-muted-foreground hover:text-foreground size-8"
                  size="icon"
                  variant="ghost"
                  onClick={onEdit}
                >
                  <Pencil className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("answer.mermaid.edit", "Edit diagram")}
              </TooltipContent>
            </Tooltip>
          )}

          {onShowCode && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="text-muted-foreground hover:text-foreground size-8"
                  size="icon"
                  variant="ghost"
                  onClick={onShowCode}
                >
                  <Code className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("answer.mermaid.show_code", "Show code")}
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-muted-foreground hover:text-foreground size-8"
                size="icon"
                variant="ghost"
                onClick={onCopy}
              >
                <Copy className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("answer.copy")}</TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="text-muted-foreground hover:text-foreground size-8"
                    size="icon"
                    variant="ghost"
                  >
                    <Download className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t("answer.download")}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onDownloadSvg}>
                <span>{t("answer.mermaid.download_svg")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDownloadPng}>
                <span>{t("answer.mermaid.download_png")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {!isModal && onExpand && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="text-muted-foreground hover:text-foreground size-8"
                  size="icon"
                  variant="ghost"
                  onClick={onExpand}
                >
                  <Maximize2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t("answer.mermaid.expand")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      {showControls && (
        <div
          className={cn(
            "bg-background/90 text-muted-foreground ring-border absolute bottom-3 left-3 rounded-lg px-2 py-1 text-xs shadow-lg ring-1 backdrop-blur-sm transition-opacity duration-200",
            showHoverControls ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          {t("answer.mermaid.nav_hint")}
        </div>
      )}

      {showControls && (
        <div
          className={cn(
            "bg-background/90 ring-border absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg p-1 shadow-lg ring-1 backdrop-blur-sm transition-opacity duration-200",
            showHoverControls ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-muted-foreground hover:text-foreground size-7"
                disabled={state.scale <= MIN_ZOOM}
                size="icon"
                variant="ghost"
                onClick={zoomOut}
              >
                <Minus className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("answer.mermaid.zoom_out")}</TooltipContent>
          </Tooltip>

          <span className="text-muted-foreground w-12 text-center text-xs font-medium tabular-nums">
            {zoomPercentage}%
          </span>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-muted-foreground hover:text-foreground size-7"
                disabled={state.scale >= MAX_ZOOM}
                size="icon"
                variant="ghost"
                onClick={zoomIn}
              >
                <Plus className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("answer.mermaid.zoom_in")}</TooltipContent>
          </Tooltip>

          <div className="bg-border mx-1 h-4 w-px" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-muted-foreground hover:text-foreground size-7"
                disabled={isAtDefaultView}
                size="icon"
                variant="ghost"
                onClick={reset}
              >
                <RotateCcw className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("answer.mermaid.reset_view")}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

type RawCodeViewProps = {
  code: string
  theme: string
  syntaxError?: string | null
  onCopy: () => void
  onEdit?: () => void
  onShowDiagram?: () => void
}

function RawCodeView({
  code,
  theme,
  syntaxError,
  onCopy,
  onEdit,
  onShowDiagram,
}: RawCodeViewProps) {
  const { t } = useTranslation()
  const [wrapLines, setWrapLines] = useState(true)

  return (
    <section className="not-prose bg-sidebar my-3 rounded-[6px] border">
      <header className="flex items-center justify-between px-[14px] pt-2">
        <p className="text-muted-foreground !m-0 text-xs font-medium">Mermaid</p>
        <div className="flex items-center gap-1">
          {onShowDiagram && !syntaxError && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="text-muted-foreground hover:text-foreground size-7"
                  size="icon"
                  variant="ghost"
                  onClick={onShowDiagram}
                >
                  <Eye className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {t("answer.mermaid.show_diagram", "Show diagram")}
              </TooltipContent>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="text-muted-foreground hover:text-foreground size-7"
                  size="icon"
                  variant="ghost"
                  onClick={onEdit}
                >
                  <Pencil className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">{t("answer.mermaid.edit", "Edit diagram")}</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-muted-foreground hover:text-foreground size-7"
                size="icon"
                variant="ghost"
                onClick={() => setWrapLines(!wrapLines)}
              >
                <WrapText
                  className={cn("size-4", wrapLines ? "text-foreground" : "text-muted-foreground")}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {wrapLines ? t("answer.disable_text_wrapping") : t("answer.enable_text_wrapping")}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-muted-foreground hover:text-foreground size-7"
                size="icon"
                variant="ghost"
                onClick={onCopy}
              >
                <Copy className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{t("answer.copy")}</TooltipContent>
          </Tooltip>
        </div>
      </header>
      {syntaxError && (
        <details className="bg-warning/30 text-warning-ink mx-[14px] mt-2 rounded text-xs">
          <summary className="cursor-pointer px-3 py-2">
            {t("answer.mermaid.syntax_error_note", "Syntax error - showing raw code")}
          </summary>
          <pre className="border-warning-ink/40 border-t px-3 py-2 text-[11px] whitespace-pre-wrap opacity-80">
            {syntaxError}
          </pre>
        </details>
      )}
      <SyntaxHighlighter
        className="!border-none !bg-transparent [&_.linenumber]:min-w-[3em] [&_.linenumber]:pl-4 [&_.prism-code]:m-0 [&_.prism-code]:py-2"
        codeTagProps={{ style: { margin: 0, padding: 0 } }}
        language="mermaid"
        style={theme === "dark" ? vscDarkPlus : vs}
        wrapLongLines={wrapLines}
      >
        {code}
      </SyntaxHighlighter>
    </section>
  )
}

export const MermaidBlock = memo(function MermaidBlock({
  code: originalCode,
  isStreaming = false,
  sessionId,
  onMermaidBlockUpdate,
  canEdit,
}: MermaidBlockProps) {
  const { t } = useTranslation()
  const { resolvedTheme: theme } = useTheme()
  const uniqueId = useId()
  // A remount (block freeze, stream completion) starts from the diagram this
  // code already rendered to, instead of an empty box and a fresh async render.
  const [initialRender] = useState(() => {
    const initialTheme: MermaidTheme = theme === "dark" ? "dark" : "default"
    const cachedSvg = getCachedMermaidSvg(originalCode, initialTheme, makeRenderId(uniqueId))
    return { theme: initialTheme, svg: cachedSvg }
  })
  const [svg, setSvg] = useState<string | null>(initialRender.svg)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showRawCode, setShowRawCode] = useState(false)
  const [selectedTheme, setSelectedTheme] = useState<MermaidTheme>(initialRender.theme)
  const lastRenderedCode = useRef<string>(initialRender.svg ? originalCode : "")
  const lastRenderedTheme = useRef<MermaidTheme | null>(
    initialRender.svg ? initialRender.theme : null
  )
  const cachedDiagramRef = useRef<{ code: string; theme: MermaidTheme } | null>(
    initialRender.svg ? { code: originalCode, theme: initialRender.theme } : null
  )
  const renderInProgress = useRef(false)
  const fontRerenderKeyRef = useRef<string | null>(null)
  const pendingRenderRef = useRef<{ code: string; theme: MermaidTheme } | null>(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const urlSessionId = useSessionId()
  const effectiveSessionId = sessionId ?? urlSessionId ?? undefined
  const chatService = ChatService.getInstance()
  const currentSession = effectiveSessionId ? chatService.getSessionById(effectiveSessionId) : null
  const canEditDiagram = canEdit ?? currentSession?.currentUserRole !== "reader"

  const code = originalCode
  const isCodeComplete = useMemo(() => isMermaidCodeComplete(code), [code])

  const isStreamingRef = useRef(isStreaming)
  isStreamingRef.current = isStreaming

  const renderMermaid = useCallback(
    async (args: { code: string; theme: MermaidTheme }) => {
      const nextCode = args.code
      const nextTheme = args.theme

      if (!nextCode.trim()) return
      if (isStreamingRef.current && !isMermaidCodeComplete(nextCode)) return
      if (lastRenderedCode.current === nextCode && lastRenderedTheme.current === nextTheme) return

      if (renderInProgress.current) {
        pendingRenderRef.current = { code: nextCode, theme: nextTheme }
        return
      }

      renderInProgress.current = true
      setError(null)
      // Don't clear SVG here - keep showing the old diagram until the new one is ready

      const cleanId = makeRenderId(uniqueId)

      try {
        const currentRenderKey = `${nextTheme}:${nextCode}`
        const didTimeoutWaitingForFont = await waitForFontLoadOrTimeout(750)

        const { svg: renderedSvg } = await renderMermaidExclusive(mermaid, {
          config: chatMermaidConfig(nextTheme),
          id: cleanId,
          code: nextCode.trim(),
        })
        setSvg(renderedSvg)
        lastRenderedCode.current = nextCode
        lastRenderedTheme.current = nextTheme
        // A render measured with fallback font metrics is re-rendered below once
        // fonts load; cache only that one, so a remount never pins clipped labels.
        if (!didTimeoutWaitingForFont) {
          // Replace this block's earlier render of other code (a partial
          // diagram while streaming, or a pre-edit version) in the same theme.
          const previous = cachedDiagramRef.current
          setCachedMermaidSvg({
            code: nextCode,
            theme: nextTheme,
            svg: renderedSvg,
            renderId: cleanId,
            replaces: previous?.theme === nextTheme ? previous : null,
          })
          cachedDiagramRef.current = { code: nextCode, theme: nextTheme }
        }

        // If our app font wasn't loaded yet (font-display: swap), Mermaid may have measured with fallback metrics.
        // Re-render once after fonts are ready to avoid clipped labels.
        if (didTimeoutWaitingForFont && fontRerenderKeyRef.current !== currentRenderKey) {
          fontRerenderKeyRef.current = currentRenderKey

          void document.fonts.ready.then(() => {
            const tryRerender = () => {
              if (renderInProgress.current) {
                setTimeout(tryRerender, 50)
                return
              }
              if (lastRenderedCode.current !== nextCode) return
              if (lastRenderedTheme.current !== nextTheme) return

              lastRenderedCode.current = ""
              void renderMermaid({ code: nextCode, theme: nextTheme })
            }

            tryRerender()
          })
        }
      } catch (err) {
        // Mermaid inserts error elements into the DOM before throwing - clean them up
        const possibleIds = [cleanId, `d${cleanId}`, `dmermaid-${cleanId}`]
        for (const elementId of possibleIds) {
          const errorElement = document.getElementById(elementId)
          if (errorElement) {
            errorElement.remove()
          }
        }
        // Also clean up any orphaned mermaid error containers
        try {
          document.querySelectorAll(`[id^="${cleanId}"]`).forEach((el) => {
            try {
              el.remove()
            } catch {
              // Element may have been removed already, continue with next
            }
          })
        } catch {
          // Cleanup failed, ignore
        }

        if (!isStreamingRef.current) {
          const errorMessage = err instanceof Error ? err.message : "Failed to render diagram"
          setError(errorMessage)
        }
        setSvg(null)
      } finally {
        renderInProgress.current = false

        const pending = pendingRenderRef.current
        pendingRenderRef.current = null
        if (pending) void renderMermaid(pending)
      }
    },
    [uniqueId]
  )

  useEffect(() => {
    // During streaming, only render once when code first becomes complete.
    // Skip if we've already rendered this exact code to avoid repeated renders
    // as subsequent text streams in after the mermaid block.
    if (isStreaming && lastRenderedCode.current === code) return
    void renderMermaid({ code, theme: selectedTheme })
  }, [code, isStreaming, renderMermaid, selectedTheme])

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(code)
    toast.success(t("answer.code_copied"))
  }, [code, t])

  const handleDownloadSvg = useCallback(() => {
    if (!svg) return
    try {
      const { svgForFile } = getSvgExportPayload(svg)
      const blob = new Blob([svgForFile], { type: "image/svg+xml;charset=utf-8" })
      downloadBlob(blob, `mermaid-diagram-${Date.now()}.svg`)
      toast.success(t("answer.mermaid.downloaded_svg"))
    } catch {
      toast.error(t("answer.mermaid.download_error"))
    }
  }, [svg, t])

  const handleDownloadPng = useCallback(async () => {
    if (!svg) return

    try {
      const { svgForDataUri, width, height } = getSvgExportPayload(svg)

      const maxCanvasDimension = 8192
      const targetScale = 2
      const scale = Math.max(1, Math.min(targetScale, maxCanvasDimension / Math.max(width, height)))

      const canvas = document.createElement("canvas")
      canvas.width = Math.round(width * scale)
      canvas.height = Math.round(height * scale)
      const ctx = canvas.getContext("2d")
      if (!ctx) throw new Error("Could not get canvas context")

      ctx.setTransform(scale, 0, 0, scale, 0, 0)
      ctx.fillStyle = "white"
      ctx.fillRect(0, 0, width, height)

      const img = new Image()
      img.crossOrigin = "anonymous"
      const dataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgForDataUri)}`

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error("Failed to load SVG"))
        img.src = dataUri
      })

      ctx.drawImage(img, 0, 0, width, height)

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (nextBlob) => {
            if (!nextBlob) {
              reject(new Error("Failed to create PNG blob"))
              return
            }
            resolve(nextBlob)
          },
          "image/png",
          1.0
        )
      })

      downloadBlob(blob, `mermaid-diagram-${Date.now()}.png`)
      toast.success(t("answer.mermaid.downloaded_png"))
    } catch {
      toast.error(t("answer.mermaid.download_error"))
    }
  }, [svg, t])

  const handleThemeChange = useCallback((newTheme: MermaidTheme) => {
    setSelectedTheme(newTheme)
  }, [])

  const openModal = useCallback(() => setIsModalOpen(true), [])

  const toggleRawCode = useCallback(() => setShowRawCode((prev) => !prev), [])

  const openEditModal = useCallback(() => {
    setIsEditModalOpen(true)
  }, [])

  const handleEditorSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (newCode: string, _title: string) => {
      if (!onMermaidBlockUpdate) {
        toast.error(t("answer.mermaid.save_not_available", "Cannot save changes"))
        return
      }

      if (newCode.trim() === originalCode.trim()) {
        setIsEditModalOpen(false)
        return
      }

      await onMermaidBlockUpdate(originalCode, newCode)
      setIsEditModalOpen(false)
    },
    [onMermaidBlockUpdate, originalCode, t]
  )

  if (!code.trim()) return null

  if (isStreaming && !isCodeComplete) return <MermaidPlaceholder />

  if (error) {
    return (
      <>
        <RawCodeView
          code={code}
          syntaxError={error}
          theme={theme}
          onCopy={handleCopy}
          onEdit={canEditDiagram && onMermaidBlockUpdate ? openEditModal : undefined}
        />
        <MermaidEditorDialog
          initialCode={originalCode}
          open={isEditModalOpen}
          showSaveButton={true}
          title={t("answer.mermaid.edit_diagram", "Edit Diagram")}
          onOpenChange={setIsEditModalOpen}
          onSave={handleEditorSave}
        />
      </>
    )
  }

  // Also covers the first frame, before the render effect has started.
  if (!svg) return <MermaidPlaceholder />

  if (showRawCode) {
    return (
      <>
        <RawCodeView
          code={code}
          theme={theme}
          onCopy={handleCopy}
          onEdit={canEditDiagram && onMermaidBlockUpdate ? openEditModal : undefined}
          onShowDiagram={toggleRawCode}
        />
        <MermaidEditorDialog
          initialCode={originalCode}
          open={isEditModalOpen}
          showSaveButton={true}
          title={t("answer.mermaid.edit_diagram", "Edit Diagram")}
          onOpenChange={setIsEditModalOpen}
          onSave={handleEditorSave}
        />
      </>
    )
  }

  return (
    <>
      <section className="not-prose my-3 overflow-hidden rounded-xl border contain-[inline-size]">
        <MermaidViewer
          autoFit
          selectedTheme={selectedTheme}
          svg={svg}
          onCopy={handleCopy}
          onDownloadPng={handleDownloadPng}
          onDownloadSvg={handleDownloadSvg}
          onEdit={canEditDiagram && onMermaidBlockUpdate ? openEditModal : undefined}
          onExpand={openModal}
          onShowCode={toggleRawCode}
          onThemeChange={handleThemeChange}
        />
      </section>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-6xl gap-3 p-4">
          <DialogHeader className="flex-row items-center justify-between space-y-0 pb-2">
            <DialogTitle className="text-base font-medium">
              {t("answer.mermaid.diagram_preview")}
            </DialogTitle>
          </DialogHeader>

          <div className="not-prose overflow-hidden rounded-xl border">
            <MermaidViewer
              autoFit
              isModal
              selectedTheme={selectedTheme}
              svg={svg}
              onCopy={handleCopy}
              onDownloadPng={handleDownloadPng}
              onDownloadSvg={handleDownloadSvg}
              onEdit={canEditDiagram && onMermaidBlockUpdate ? openEditModal : undefined}
              onThemeChange={handleThemeChange}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal - Full Mermaid Editor */}
      <MermaidEditorDialog
        initialCode={originalCode}
        open={isEditModalOpen}
        showSaveButton={true}
        title={t("answer.mermaid.edit_diagram", "Edit Diagram")}
        onOpenChange={setIsEditModalOpen}
        onSave={handleEditorSave}
      />
    </>
  )
})
